import React, { useState, useRef, useEffect } from 'react';
import PropTypes from 'prop-types';
import Skeleton from '../Skeleton';
import styles from './SelectDropdown.module.scss';

const SelectDropdown = ({
  label,
  options = [],
  value,
  onChange,
  placeholder = 'Select an option',
  required = false,
  disabled = false,
  error = false,
  errorMessage = '',
  helperText = '',
  isLoading = false,
  name,
  className = '',
  ...rest
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const dropdownRef = useRef(null);
  const triggerRef = useRef(null);

  const selectedOption = options.find((opt) => opt.value === value);

  const handleToggle = () => {
    if (!disabled) {
      setIsOpen(!isOpen);
    }
  };

  const handleSelect = (option) => {
    if (!option.disabled) {
      if (onChange) {
        onChange(option.value, name);
      }
      setIsOpen(false);
      triggerRef.current?.focus();
    }
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Escape') {
      setIsOpen(false);
      triggerRef.current?.focus();
    } else if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault();
      handleToggle();
    }
  };

  const handleOptionKeyDown = (e, option) => {
    if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault();
      handleSelect(option);
    }
  };

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (
        dropdownRef.current &&
        !dropdownRef.current.contains(event.target) &&
        triggerRef.current &&
        !triggerRef.current.contains(event.target)
      ) {
        setIsOpen(false);
      }
    };

    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isOpen]);

  const triggerClasses = [
    styles.selectTrigger,
    isOpen && styles.open,
    !selectedOption && styles.placeholder,
    error && styles.error,
  ]
    .filter(Boolean)
    .join(' ');

  return (
    <div className={`${styles.selectWrapper} ${className}`} {...rest}>
      {label && (
        <label className={styles.label} htmlFor={name}>
          {label}
          {required && <span className={styles.required}>*</span>}
        </label>
      )}
      <div className={styles.selectContainer}>
        {isLoading ? (
          <Skeleton height="44px" borderRadius="8px" />
        ) : (
          <>
            <button
              ref={triggerRef}
              id={name}
              type="button"
              className={triggerClasses}
              onClick={handleToggle}
              onKeyDown={handleKeyDown}
              disabled={disabled}
              aria-haspopup="listbox"
              aria-expanded={isOpen}
              aria-labelledby={label ? `${name}-label` : undefined}
              aria-invalid={error}
            >
              <span className={styles.selectedValue}>
                {selectedOption ? selectedOption.label : placeholder}
              </span>
              <span className={`${styles.icon} ${isOpen ? styles.rotated : ''}`}>▼</span>
            </button>
            {isOpen && (
              <div ref={dropdownRef} className={styles.dropdown} role="listbox">
                {options.map((option) => {
                  const isSelected = option.value === value;
                  const optionClasses = [
                    styles.option,
                    isSelected && styles.selected,
                  ]
                    .filter(Boolean)
                    .join(' ');

                  return (
                    <button
                      key={option.value}
                      type="button"
                      className={optionClasses}
                      onClick={() => handleSelect(option)}
                      onKeyDown={(e) => handleOptionKeyDown(e, option)}
                      disabled={option.disabled}
                      role="option"
                      aria-selected={isSelected}
                    >
                      {option.label}
                    </button>
                  );
                })}
              </div>
            )}
          </>
        )}
      </div>
      {error && errorMessage && (
        <div id={`${name}-error`} className={styles.errorMessage}>
          {errorMessage}
        </div>
      )}
      {!error && helperText && (
        <div id={`${name}-helper`} className={styles.helperText}>
          {helperText}
        </div>
      )}
    </div>
  );
};

SelectDropdown.propTypes = {
  label: PropTypes.string,
  options: PropTypes.arrayOf(
    PropTypes.shape({
      value: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
      label: PropTypes.string.isRequired,
      disabled: PropTypes.bool,
    })
  ),
  value: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  onChange: PropTypes.func,
  placeholder: PropTypes.string,
  required: PropTypes.bool,
  disabled: PropTypes.bool,
  error: PropTypes.bool,
  errorMessage: PropTypes.string,
  helperText: PropTypes.string,
  isLoading: PropTypes.bool,
  name: PropTypes.string,
  className: PropTypes.string,
};

export default SelectDropdown;
